import { useEffect, useState } from 'react';
import TestBreakdownTable from './tables/TestBreakdownTable';
import TableHeader from '../../components/generic/TableHeader';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import ComparativeTestPerformanceTable from './tables/ComparativeTestPerformanceTable';
import ComparativeTestBreakdownTable from './tables/ComparativeTestBreakdownTable';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function PerformanceAnalysis({ isComparative, comparativeData, totalMarks, performanceData, isDemo }) {
  const [performance, setPerformance] = useState(null);
  const [comparativeAccuracy, setComparativeAccuracy] = useState(null);
  const [comparativeScore, setComparativeScore] = useState(null);
  const [comparativeTestBreakdown, setComparativeTestBreakdown] = useState(null);
  const [userAccuracy, setUserAccuracy] = useState(null);
  const [userScore, setUserScore] = useState(null);
  const [userTestBreakdown, setUserTestBreakdown] = useState(null);
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isDemo || !performanceData) return;

    setIsLoading(true);

    try {
      const cloned = structuredClone(performanceData.performance);
      const meta = performanceData.subjectMeta;

      // Merge testPerformance into testBreakdown (same as your API logic)
      for (const subject in cloned.testPerformance) {
        if (cloned.testBreakdown[subject]) {
          cloned.testBreakdown[subject] = {
            ...cloned.testBreakdown[subject],
            ...cloned.testPerformance[subject],
          };
        }
      }

      // Convert to array + attach Meta
      const result = Object.entries(cloned.testBreakdown)
        .map(([subject, data]) => ({
          subject,
          ...data,
          ...meta[subject],
        }))
        .sort((a, b) => {
          if (a.subject === 'overall') return -1;
          if (b.subject === 'overall') return 1;
          return 0;
        });

      setPerformance(result);
    } catch (error) {
      console.error('Mock data processing error:', error);
    }

    setIsLoading(false);
  }, [isDemo, performanceData]);

  useEffect(() => {
    if (!comparativeData || !isComparative) return;

    setComparativeAccuracy(comparativeData?.comparativeData?.accuracy);
    setComparativeScore(comparativeData?.comparativeData?.scores);
    setComparativeTestBreakdown(comparativeData?.comparativeData?.test_breakdown);

    setUserAccuracy(comparativeData?.userData?.accuracy);
    setUserScore(comparativeData?.userData?.scores);
    setUserTestBreakdown(comparativeData?.userData?.test_breakdown);

    let meta = structuredClone(comparativeData?.subjectMeta);

    if (Object.keys(meta).length > 2) {
      const overall = { Overall: meta['Overall'] };
      delete meta['Overall'];
      meta = { ...overall, ...meta };
    } else {
      delete meta['Overall'];
    }

    setSubjectMeta(meta);
    setSelectedSubject(Object.values(meta)[0]);
  }, [isComparative, comparativeData]);

  const handleSubjectChange = (subject) => setSelectedSubject(subject);

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && subjectMeta)
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={'Test Performance'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>

          <ComparativeTestPerformanceTable
            comparativeAccuracy={comparativeAccuracy}
            comparativeScore={comparativeScore}
            userAccuracy={userAccuracy}
            userScore={userScore}
            totalMarks={totalMarks}
            selectedSubject={selectedSubject}
          />
        </TableHeader>

        <TableHeader headerTitle={'Test Breakdown'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta)?.map((tab) => (
                <Tab
                  key={tab?._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => handleSubjectChange(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} />}
                  <p className="mb-0">{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>

          <ComparativeTestBreakdownTable
            comparativeTestBreakdown={comparativeTestBreakdown}
            userTestBreakdown={userTestBreakdown}
            totalQuestions={comparativeData?.totalQuestions}
            selectedSubject={selectedSubject}
          />
        </TableHeader>
      </div>
    );

  // ⭐ DEFAULT NON-COMPARATIVE VIEW (Mock Data)
  return (
    <TableHeader headerTitle={'Test Breakdown'}>
      <TestBreakdownTable performance={performance} />
    </TableHeader>
  );
}
